/**
 * Bot Config Service
 * Generic CRUD for custom LLM bot configurations
 */

import { createBotConfig } from './bot-config.js'

/**
 * Create bot config service
 * @param {Object} storageAdapter - Storage adapter with get/set methods
 * @param {Object} options
 * @param {string} options.storageKey - Storage key for persisting bots
 * @param {Object[]} [options.builtInBots] - Built-in bot configs (immutable, prepended to list)
 */
export function createBotConfigService(storageAdapter, { storageKey, builtInBots = [] }) {
  const builtInIds = new Set(builtInBots.map(b => b.botId))

  const loadBots = async () => {
    const stored = await storageAdapter.get(storageKey)
    if (!stored) return []
    try {
      return JSON.parse(stored)
    } catch (err) {
      console.warn('Failed to parse bot config from storage, resetting:', err.message)
      return []
    }
  }

  const saveBots = async (bots) => {
    await storageAdapter.set(storageKey, JSON.stringify(bots))
  }

  const getBots = async () => {
    const stored = await loadBots()
    const merged = builtInBots.map(b => {
      const overrides = stored.find(s => s.botId === b.botId)
      return overrides ? { ...b, ...overrides, botId: b.botId, isBuiltIn: true } : b
    })
    return [...merged, ...stored.filter(b => !builtInIds.has(b.botId))]
  }

  const getBot = async (botId) => {
    const builtIn = builtInBots.find(b => b.botId === botId)
    if (builtIn) {
      const stored = await loadBots()
      const overrides = stored.find(b => b.botId === botId)
      return overrides ? { ...builtIn, ...overrides, botId, isBuiltIn: true } : builtIn
    }
    const bots = await loadBots()
    return bots.find(b => b.botId === botId) || null
  }

  const createBot = async (config) => {
    const bot = createBotConfig(config)
    const bots = await loadBots()
    bots.push(bot)
    await saveBots(bots)
    return bot
  }

  const updateBot = async (botId, updates) => {
    const bots = await loadBots()
    if (builtInIds.has(botId)) {
      // Built-in bot: store overrides (merged on read)
      const existing = bots.find(b => b.botId === botId)
      const overrides = { ...(existing || {}), ...updates, botId }
      if (existing) {
        const idx = bots.indexOf(existing)
        bots[idx] = overrides
      } else {
        bots.push(overrides)
      }
      await saveBots(bots)
      const builtIn = builtInBots.find(b => b.botId === botId)
      return { ...builtIn, ...overrides, isBuiltIn: true }
    }
    const idx = bots.findIndex(b => b.botId === botId)
    if (idx < 0) throw new Error('Bot not found')
    bots[idx] = { ...bots[idx], ...updates, botId }
    await saveBots(bots)
    return bots[idx]
  }

  const deleteBot = async (botId) => {
    if (builtInIds.has(botId)) throw new Error('Cannot delete built-in bot')
    const bots = await loadBots()
    await saveBots(bots.filter(b => b.botId !== botId))
  }

  return { getBots, getBot, createBot, updateBot, deleteBot }
}
